import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Loader2,
  Plus,
  RefreshCw,
  Settings2,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { bannerTypesService, inventoryService } from '@/services/inventory';
import { cn } from '@/lib/utils';
import type { BannerSurface, BannerType, ItemBanner } from '@/types/inventory';

const SURFACE_LABELS: Record<BannerSurface, string> = {
  LISTING_CARD: 'Listing card',
  PRODUCT_PAGE: 'Product page',
  CATEGORY: 'Category',
};

/** Parse "4:5" → 0.8 (w/h), or null if unparseable. */
function ratioOf(aspect: string): number | null {
  const m = /^(\d{1,3}):(\d{1,3})$/.exec(aspect.trim());
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  return w > 0 && h > 0 ? w / h : null;
}

import { BannerTypesManager } from './BannerTypesManager';
import { ImageCropDialog } from './ImageCropDialog';
import { IMAGE_MAX_BYTES, imageTooLargeMessage } from './imageProfile';

interface Props {
  itemId: string | null;
}

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function validateFile(file: File): string | null {
  if (!ACCEPTED.includes(file.type)) {
    return `Type ${file.type || 'unknown'} not supported. Use JPEG, PNG, WebP or GIF.`;
  }
  if (file.size > IMAGE_MAX_BYTES) return imageTooLargeMessage();
  return null;
}

/**
 * Portal banners — banners grouped by the tenant's banner types, each type a
 * collapsible row (so it stays manageable with many types). Header shows a
 * thumbnail strip + count at a glance; expand to add/reorder/replace/remove.
 */
export function ItemBannersTab({ itemId }: Props) {
  const [manageOpen, setManageOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState(false);

  const typesQuery = useQuery({
    queryKey: ['inventory', 'banner-types'],
    queryFn: () => bannerTypesService.list(),
    enabled: !!itemId,
  });
  const bannersQuery = useQuery({
    queryKey: ['inventory', 'banners', itemId],
    queryFn: () => inventoryService.listBanners(itemId!),
    enabled: !!itemId,
  });

  const types = typesQuery.data ?? [];

  // Expand the first type by default so the tab isn't all-collapsed on open.
  useEffect(() => {
    if (!seeded && types.length > 0) {
      setExpanded(new Set([types[0].id]));
      setSeeded(true);
    }
  }, [seeded, types]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (!itemId) {
    return (
      <div className="rounded-md border border-dashed bg-muted/30 py-10 text-center">
        <ImageIcon className="mx-auto h-5 w-5 text-muted-foreground/60" />
        <div className="mt-2 text-[13px] font-medium text-foreground">Save the item first</div>
        <div className="mx-auto mt-1 max-w-sm text-[12px] text-muted-foreground">
          Once the item is created you can add banner images for each of your banner types.
        </div>
      </div>
    );
  }

  const banners = bannersQuery.data ?? [];
  const byType = (id: string) => banners.filter((b) => b.bannerTypeId === id);
  const orphans = banners.filter(
    (b) => !b.bannerTypeId || !types.some((t) => t.id === b.bannerTypeId),
  );
  const loading = typesQuery.isLoading || bannersQuery.isLoading;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-md text-[11.5px] leading-relaxed text-muted-foreground">
          Storefront images grouped by placement (each a fixed aspect ratio). Click one to expand and
          add images (any number — a carousel), reorder with ←/→, replace or remove. A placement set to
          <span className="font-medium text-foreground"> Listing card</span> is what the storefront shows
          on product cards.
        </p>
        <Button variant="outline" size="sm" onClick={() => setManageOpen(true)}>
          <Settings2 className="h-3.5 w-3.5" /> Manage types
        </Button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : types.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/30 py-8 text-center">
          <div className="text-[13px] font-medium text-foreground">No banner types yet</div>
          <div className="mx-auto mt-1 max-w-sm text-[12px] text-muted-foreground">
            Define a banner type (name, aspect ratio, size, display) to start adding banners.
          </div>
          <Button size="sm" className="mt-3" onClick={() => setManageOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> Add a banner type
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-border/60 overflow-hidden rounded-md border border-border/60">
          {types.map((t) => (
            <TypeRow
              key={t.id}
              itemId={itemId}
              type={t}
              banners={byType(t.id)}
              open={expanded.has(t.id)}
              onToggle={() => toggle(t.id)}
            />
          ))}
        </div>
      )}

      {orphans.length > 0 && (
        <section className="space-y-2 rounded-md border border-border/60 p-3">
          <div className="flex items-baseline gap-2">
            <span className="text-[12.5px] font-medium text-foreground">Unassigned</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              no type — remove or re-upload under a type
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            {orphans.map((b) => (
              <BannerCard key={b.id} itemId={itemId} banner={b} idx={0} total={1} onMove={() => {}} reordering={false} />
            ))}
          </div>
        </section>
      )}

      <BannerTypesManager open={manageOpen} onOpenChange={setManageOpen} />
    </div>
  );
}

function TypeRow({
  itemId,
  type,
  banners,
  open,
  onToggle,
}: {
  itemId: string;
  type: BannerType;
  banners: ItemBanner[];
  open: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // A picked image awaiting crop-to-aspect before upload (placements with a
  // fixed ratio) — so the uploaded image is always the exact required ratio.
  const [cropFile, setCropFile] = useState<File | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventory', 'banners', itemId] });

  const upload = useMutation({
    mutationFn: (file: File) => inventoryService.uploadBanner(itemId, type.id, file),
    onSuccess: () => {
      invalidate();
      toast.success(`${type.name} banner added`);
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Upload failed'),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => inventoryService.reorderBanners(itemId, ids),
    onSuccess: () => invalidate(),
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not reorder'),
  });

  const move = (idx: number, dir: -1 | 1) => {
    const ids = banners.map((b) => b.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    reorder.mutate(ids);
  };

  const pick = (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return;
    }
    // Placements with a fixed ratio open the crop-to-aspect dialog, so the
    // uploaded image is ALWAYS the exact required ratio (user pans/zooms to
    // frame it). Placements without a parseable ratio upload directly.
    const target = ratioOf(type.aspectRatio);
    if (target) {
      setCropFile(file);
      return;
    }
    upload.mutate(file);
  };

  return (
    <div>
      {/* Header — always visible, click to expand */}
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className="shrink-0 text-[13px] font-medium text-foreground">{type.name}</span>
          {type.showsOn ? (
            <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {SURFACE_LABELS[type.showsOn]}
            </span>
          ) : null}
          <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
            {type.aspectRatio} · {type.width}×{type.height}
          </span>
          {type.display && (
            <span className="hidden truncate text-[11px] text-muted-foreground sm:inline">
              · {type.display}
            </span>
          )}
        </button>

        {/* Collapsed peek: thumbnail strip + count */}
        {!open && (
          <div className="flex items-center gap-1.5">
            <div className="flex -space-x-1">
              {banners.slice(0, 4).map((b) => (
                <img
                  key={b.id}
                  src={b.url}
                  alt=""
                  className="h-7 w-10 rounded border border-background object-cover shadow-sm"
                />
              ))}
            </div>
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {banners.length === 0 ? 'empty' : banners.length}
            </span>
          </div>
        )}

        <Button
          size="sm"
          variant="ghost"
          className="h-7 shrink-0"
          onClick={() => {
            if (!open) onToggle();
            inputRef.current?.click();
          }}
          disabled={upload.isPending}
        >
          {upload.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Add
        </Button>
      </div>

      {/* Body — expanded */}
      {open && (
        <div
          className="px-3 pb-3"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            pick(e.dataTransfer.files);
          }}
        >
          {banners.length === 0 ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className={cn(
                'flex h-24 w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed text-center transition-colors',
                dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:border-primary/50',
              )}
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
              <span className="text-[11px] text-muted-foreground">
                Drop an image or <span className="font-medium text-primary">choose a file</span>
              </span>
            </button>
          ) : (
            <div className="flex flex-wrap gap-3">
              {banners.map((b, idx) => (
                <BannerCard
                  key={b.id}
                  itemId={itemId}
                  banner={b}
                  idx={idx}
                  total={banners.length}
                  onMove={move}
                  reordering={reorder.isPending}
                />
              ))}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className={cn(
                  'flex h-24 w-40 flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed text-center transition-colors',
                  dragOver ? 'border-primary bg-primary/5' : 'border-border bg-muted/30 hover:border-primary/50',
                )}
              >
                {upload.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                ) : (
                  <Plus className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-[11px] font-medium text-primary">Add banner</span>
              </button>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />

      {/* Crop-to-aspect dialog — guarantees the exact required ratio */}
      <ImageCropDialog
        file={cropFile}
        aspect={ratioOf(type.aspectRatio) ?? 1}
        targetWidth={type.width}
        targetHeight={type.height}
        aspectLabel={type.aspectRatio}
        placementName={type.name}
        onCancel={() => setCropFile(null)}
        onCropped={(f) => {
          upload.mutate(f);
          setCropFile(null);
        }}
      />
    </div>
  );
}

function BannerCard({
  itemId,
  banner,
  idx,
  total,
  onMove,
  reordering,
}: {
  itemId: string;
  banner: ItemBanner;
  idx: number;
  total: number;
  onMove: (idx: number, dir: -1 | 1) => void;
  reordering: boolean;
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventory', 'banners', itemId] });

  const replace = useMutation({
    mutationFn: (file: File) => inventoryService.replaceBanner(itemId, banner.id, file),
    onSuccess: () => {
      invalidate();
      toast.success('Banner replaced');
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Replace failed'),
  });

  const remove = useMutation({
    mutationFn: () => inventoryService.deleteBanner(itemId, banner.id),
    onSuccess: () => {
      invalidate();
      toast.success('Banner removed');
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Could not remove'),
  });

  const busy = replace.isPending || remove.isPending || reordering;

  const onPick = (files: FileList | null | undefined) => {
    const file = files?.[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return;
    }
    replace.mutate(file);
  };

  return (
    <div className="w-40 space-y-1">
      <div className="relative h-24 w-40 overflow-hidden rounded-md border border-border/60 bg-muted">
        <img src={banner.url} alt="banner" className="h-full w-full object-cover" />
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-background/60">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0.5">
          {total > 1 && (
            <>
              <IconBtn label="Move left" disabled={busy || idx === 0} onClick={() => onMove(idx, -1)}>
                <ChevronLeft className="h-3.5 w-3.5" />
              </IconBtn>
              <IconBtn label="Move right" disabled={busy || idx === total - 1} onClick={() => onMove(idx, 1)}>
                <ChevronRight className="h-3.5 w-3.5" />
              </IconBtn>
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <IconBtn label="Replace" disabled={busy} onClick={() => inputRef.current?.click()}>
            <RefreshCw className="h-3.5 w-3.5" />
          </IconBtn>
          <IconBtn label="Remove" disabled={busy} danger onClick={() => remove.mutate()}>
            <Trash2 className="h-3.5 w-3.5" />
          </IconBtn>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn('h-6 w-6', danger && 'text-destructive hover:text-destructive')}
    >
      {children}
    </Button>
  );
}
